import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  BarChart3, PieChart as PieChartIcon, TrendingUp, Download, FileText, Users,
  DollarSign, Clock, MapPin, Ticket, Building, Briefcase, Calendar,
  RefreshCw, ArrowUpRight, ArrowDownRight, Minus, Filter, ChevronDown
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import { useCurrency } from '../contexts/CurrencyContext';

const API = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

const MetricCard = ({ icon: Icon, label, value, sub, color = 'emerald', trend }) => (
  <Card className="bg-white border-gray-border" data-testid={`metric-${label?.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-medium truncate">{label}</p>
          <p className="text-xl font-bold text-text-primary mt-1">{value}</p>
          {sub && <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>}
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", `bg-${color}-100`)}>
            <Icon className={cn("h-4 w-4", `text-${color}-600`)} />
          </div>
          {trend !== undefined && (
            <div className={cn("flex items-center gap-0.5 text-[10px] font-medium",
              trend > 0 ? "text-emerald-600" : trend < 0 ? "text-red-500" : "text-gray-400"
            )}>
              {trend > 0 ? <ArrowUpRight className="h-3 w-3" /> : trend < 0 ? <ArrowDownRight className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
              {Math.abs(trend)}%
            </div>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

const ChartCard = ({ title, children, className }) => (
  <Card className={cn("bg-white border-gray-border", className)}>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-heading text-text-primary">{title}</CardTitle>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-medium text-text-primary mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-gray-500">{p.name}:</span>
          <span className="font-medium">{typeof p.value === 'number' ? p.value.toLocaleString() : p.value}</span>
        </p>
      ))}
    </div>
  );
};

function exportCSV(data, filename) {
  if (!data?.length) { toast.error('No data to export'); return; }
  const headers = Object.keys(data[0]);
  const csv = [headers.join(','), ...data.map(row => headers.map(h => {
    const v = row[h];
    return typeof v === 'string' && v.includes(',') ? `"${v}"` : v ?? '';
  }).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${filename}.csv`; a.click();
  URL.revokeObjectURL(url);
  toast.success('CSV exported');
}

async function downloadPDF(reportType, params = {}) {
  try {
    toast.info('Generating PDF...');
    const query = new URLSearchParams(params).toString();
    const res = await axios.get(`${API}/api/reports/pdf/${reportType}${query ? '?' + query : ''}`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url; a.download = `${reportType}_report.pdf`; a.click();
    URL.revokeObjectURL(url);
    toast.success('PDF downloaded');
  } catch {
    toast.error('PDF generation failed');
  }
}

const ExportButtons = ({ csvData, csvFilename, pdfType, dateFrom, dateTo }) => (
  <div className="flex justify-end gap-2">
    <Button size="sm" variant="outline" onClick={() => exportCSV(csvData || [], csvFilename)} data-testid={`export-${csvFilename}-csv`}>
      <Download className="h-3.5 w-3.5 mr-1.5" /> CSV
    </Button>
    <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
      onClick={() => downloadPDF(pdfType, { date_from: dateFrom, date_to: dateTo })} data-testid={`export-${pdfType}-pdf`}>
      <FileText className="h-3.5 w-3.5 mr-1.5" /> PDF
    </Button>
  </div>
);

// ─── Executive Summary Tab ──────────────────────────────────────────────────
function ExecutiveSummary({ dateFrom, dateTo }) {
  const { format: fc } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/api/reports/summary`, { params: { date_from: dateFrom, date_to: dateTo } });
      setData(d);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{[1,2,3,4,5,6,7,8].map(i => <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />)}</div>;
  if (!data) return <p className="text-sm text-gray-400 text-center py-8">No data available</p>;

  const summaryData = [
    { label: 'Revenue', value: fc(data.revenue?.total_invoiced || 0), sub: `Paid: ${fc(data.revenue?.total_paid || 0)}`, icon: DollarSign, color: 'emerald' },
    { label: 'Field Jobs', value: data.field_jobs?.total || 0, sub: `${data.field_jobs?.rate || 0}% completion`, icon: MapPin, color: 'blue' },
    { label: 'Clients', value: data.clients?.total || 0, icon: Building, color: 'purple' },
    { label: 'Employees', value: data.employees?.active || 0, icon: Users, color: 'cyan' },
    { label: 'Tickets', value: data.tickets?.total || 0, sub: `${data.tickets?.open || 0} open`, icon: Ticket, color: 'amber' },
    { label: 'Attendance', value: data.attendance?.records || 0, sub: 'Check-in records', icon: Clock, color: 'rose' },
    { label: 'Invoices', value: data.invoices?.total || 0, icon: FileText, color: 'teal' },
    { label: 'Job Rate', value: `${data.field_jobs?.rate || 0}%`, sub: 'Completion rate', icon: TrendingUp, color: 'lime' },
  ];

  const pieData = [
    { name: 'Completed', value: data.field_jobs?.completed || 0 },
    { name: 'Open', value: (data.field_jobs?.total || 0) - (data.field_jobs?.completed || 0) },
  ].filter(d => d.value > 0);

  const ticketPie = [
    { name: 'Open', value: data.tickets?.open || 0 },
    { name: 'Resolved', value: (data.tickets?.total || 0) - (data.tickets?.open || 0) },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-4" data-testid="executive-summary">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {summaryData.map((m, i) => <MetricCard key={i} {...m} />)}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ChartCard title="Field Jobs Breakdown">
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} innerRadius={40} paddingAngle={4} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-gray-400 text-center py-12">No field job data</p>}
        </ChartCard>
        <ChartCard title="Tickets Overview">
          {ticketPie.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={ticketPie} cx="50%" cy="50%" outerRadius={80} innerRadius={40} paddingAngle={4} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {ticketPie.map((_, i) => <Cell key={i} fill={[COLORS[4], COLORS[0]][i] || COLORS[i]} />)}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-gray-400 text-center py-12">No ticket data</p>}
        </ChartCard>
      </div>
      <ExportButtons csvData={[]} csvFilename="summary" pdfType="summary" dateFrom={dateFrom} dateTo={dateTo} />
    </div>
  );
}

// ─── Revenue Tab ────────────────────────────────────────────────────────────
function RevenueReport({ dateFrom, dateTo }) {
  const { format: fc } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const { data: d } = await axios.get(`${API}/api/reports/revenue`, { params: { date_from: dateFrom, date_to: dateTo } }); setData(d); }
    catch { setData(null); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <div className="h-60 bg-gray-100 rounded-xl animate-pulse" />;
  if (!data) return <p className="text-xs text-gray-400 text-center py-8">No revenue data</p>;

  return (
    <div className="space-y-4" data-testid="revenue-report">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard icon={DollarSign} label="Total Invoiced" value={fc(data.total_revenue || 0)} color="emerald" />
        <MetricCard icon={DollarSign} label="Total Paid" value={fc(data.total_paid || 0)} color="blue" />
        <MetricCard icon={DollarSign} label="Outstanding" value={fc(data.outstanding || 0)} color="amber" />
        <MetricCard icon={FileText} label="Invoices" value={data.total_invoices} color="purple" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ChartCard title="Monthly Revenue Trend">
          {data.monthly_trend?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={data.monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="total" name="Invoiced" fill="#10B981" fillOpacity={0.15} stroke="#10B981" strokeWidth={2} />
                <Area type="monotone" dataKey="paid" name="Paid" fill="#3B82F6" fillOpacity={0.1} stroke="#3B82F6" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-gray-400 text-center py-12">No monthly data</p>}
        </ChartCard>
        <ChartCard title="Invoice Status">
          {data.by_status?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.by_status}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="status" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="Count" fill="#10B981" radius={[4, 4, 0, 0]}>
                  {data.by_status.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-gray-400 text-center py-12">No status data</p>}
        </ChartCard>
      </div>
      <ExportButtons csvData={data.monthly_trend} csvFilename="revenue_report" pdfType="revenue" dateFrom={dateFrom} dateTo={dateTo} />
    </div>
  );
}

// ─── Attendance Tab ─────────────────────────────────────────────────────────
function AttendanceReport({ dateFrom, dateTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const { data: d } = await axios.get(`${API}/api/reports/attendance`, { params: { date_from: dateFrom, date_to: dateTo } }); setData(d); }
    catch { setData(null); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <div className="h-60 bg-gray-100 rounded-xl animate-pulse" />;
  if (!data) return <p className="text-xs text-gray-400 text-center py-8">No attendance data</p>;

  return (
    <div className="space-y-4" data-testid="attendance-report">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard icon={Clock} label="Total Records" value={data.total_records} color="blue" />
        <MetricCard icon={Clock} label="Total Hours" value={`${data.total_hours?.toFixed(1)}h`} color="emerald" />
        <MetricCard icon={Clock} label="Overtime" value={`${data.total_overtime?.toFixed(1)}h`} color="amber" />
        <MetricCard icon={Clock} label="Late Instances" value={data.total_late_instances} color="red" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ChartCard title="Daily Attendance Trend">
          {data.daily_trend?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={data.daily_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 9 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="present" name="Present" fill="#10B981" fillOpacity={0.2} stroke="#10B981" strokeWidth={2} />
                <Area type="monotone" dataKey="late" name="Late" fill="#EF4444" fillOpacity={0.1} stroke="#EF4444" strokeWidth={1.5} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-gray-400 text-center py-12">No daily trend data</p>}
        </ChartCard>
        <ChartCard title="Employee Summary">
          {data.by_employee?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.by_employee.slice(0, 10)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="employee_name" tick={{ fontSize: 9 }} width={100} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="total_hours" name="Hours" fill="#3B82F6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-gray-400 text-center py-12">No employee data</p>}
        </ChartCard>
      </div>
      <ExportButtons csvData={data.by_employee} csvFilename="attendance_report" pdfType="attendance" dateFrom={dateFrom} dateTo={dateTo} />
    </div>
  );
}

// ─── Field Jobs Tab ─────────────────────────────────────────────────────────
function FieldJobsReport({ dateFrom, dateTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const { data: d } = await axios.get(`${API}/api/reports/field-jobs`, { params: { date_from: dateFrom, date_to: dateTo } }); setData(d); }
    catch { setData(null); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <div className="h-60 bg-gray-100 rounded-xl animate-pulse" />;
  if (!data) return <p className="text-xs text-gray-400 text-center py-8">No field jobs data</p>;

  return (
    <div className="space-y-4" data-testid="field-jobs-report">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard icon={MapPin} label="Total Jobs" value={data.total_jobs} color="blue" />
        <MetricCard icon={TrendingUp} label="Completion Rate" value={`${data.completion_rate}%`} color="emerald" />
        <MetricCard icon={BarChart3} label="Avg Rating" value={data.avg_rating || 'N/A'} color="amber" />
        <MetricCard icon={Users} label="Technicians" value={data.by_technician?.length || 0} color="purple" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ChartCard title="Jobs by Status">
          {data.by_status?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={data.by_status.map(s => ({ name: s.status, value: s.count }))} cx="50%" cy="50%" outerRadius={80} innerRadius={40} paddingAngle={4} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {data.by_status.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-gray-400 text-center py-12">No status data</p>}
        </ChartCard>
        <ChartCard title="Top Technicians">
          {data.by_technician?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.by_technician.slice(0, 8)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 9 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="total_jobs" name="Total" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="completed" name="Completed" fill="#10B981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-gray-400 text-center py-12">No technician data</p>}
        </ChartCard>
      </div>
      <ExportButtons csvData={data.by_technician} csvFilename="field_jobs_report" pdfType="field-jobs" dateFrom={dateFrom} dateTo={dateTo} />
    </div>
  );
}

// ─── HR Report Tab ──────────────────────────────────────────────────────────
function HRReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const { data: d } = await axios.get(`${API}/api/reports/hr`); setData(d); }
    catch { setData(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <div className="h-60 bg-gray-100 rounded-xl animate-pulse" />;
  if (!data) return <p className="text-xs text-gray-400 text-center py-8">No HR data</p>;

  return (
    <div className="space-y-4" data-testid="hr-report">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard icon={Users} label="Total Employees" value={data.total_employees} color="blue" />
        <MetricCard icon={Calendar} label="Leave Requests" value={data.leave_total} color="purple" />
        <MetricCard icon={Clock} label="Pending Leaves" value={data.leave_pending} color="amber" />
        <MetricCard icon={Briefcase} label="Approved Leaves" value={data.leave_approved} color="emerald" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ChartCard title="By Department">
          {data.by_department?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.by_department}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="department" tick={{ fontSize: 9 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="Employees" fill="#8B5CF6" radius={[4, 4, 0, 0]}>
                  {data.by_department.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-gray-400 text-center py-12">No department data</p>}
        </ChartCard>
        <ChartCard title="Employment Type">
          {data.by_employment_type?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={data.by_employment_type.map(t => ({ name: t.type || 'Unknown', value: t.count }))} cx="50%" cy="50%" outerRadius={80} innerRadius={40} paddingAngle={4} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {data.by_employment_type.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-gray-400 text-center py-12">No type data</p>}
        </ChartCard>
      </div>
      <ExportButtons csvData={data.by_department} csvFilename="hr_report" pdfType="hr" />
    </div>
  );
}

// ─── Tickets Report Tab ─────────────────────────────────────────────────────
function TicketsReport({ dateFrom, dateTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const { data: d } = await axios.get(`${API}/api/reports/tickets`, { params: { date_from: dateFrom, date_to: dateTo } }); setData(d); }
    catch { setData(null); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <div className="h-60 bg-gray-100 rounded-xl animate-pulse" />;
  if (!data) return <p className="text-xs text-gray-400 text-center py-8">No ticket data</p>;

  return (
    <div className="space-y-4" data-testid="tickets-report">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard icon={Ticket} label="Total Tickets" value={data.total_tickets} color="blue" />
        <MetricCard icon={TrendingUp} label="Resolution Rate" value={`${data.resolution_rate}%`} color="emerald" />
        <MetricCard icon={BarChart3} label="Sources" value={data.by_source?.length || 0} color="purple" />
        <MetricCard icon={Briefcase} label="Categories" value={data.by_category?.length || 0} color="amber" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ChartCard title="Tickets by Status">
          {data.by_status?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.by_status}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="status" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="Tickets" fill="#F59E0B" radius={[4, 4, 0, 0]}>
                  {data.by_status.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-gray-400 text-center py-12">No status data</p>}
        </ChartCard>
        <ChartCard title="Tickets by Priority">
          {data.by_priority?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={data.by_priority.map(p => ({ name: p.priority || 'N/A', value: p.count }))} cx="50%" cy="50%" outerRadius={80} innerRadius={40} paddingAngle={4} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {data.by_priority.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-gray-400 text-center py-12">No priority data</p>}
        </ChartCard>
      </div>
      <ExportButtons csvData={data.by_status} csvFilename="tickets_report" pdfType="tickets" dateFrom={dateFrom} dateTo={dateTo} />
    </div>
  );
}

// ─── Main Dashboard ─────────────────────────────────────────────────────────
export default function UniversalReportDashboardPage() {
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [quickRange, setQuickRange] = useState('30d');

  const applyQuickRange = (range) => {
    setQuickRange(range);
    const now = new Date();
    const to = now.toISOString().split('T')[0];
    let from;
    switch (range) {
      case '7d': from = new Date(now - 7 * 86400000); break;
      case '30d': from = new Date(now - 30 * 86400000); break;
      case '90d': from = new Date(now - 90 * 86400000); break;
      case '1y': from = new Date(now - 365 * 86400000); break;
      default: from = new Date(now - 30 * 86400000);
    }
    setDateFrom(from.toISOString().split('T')[0]);
    setDateTo(to);
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="universal-report-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">
            Report Dashboard<PageInfoTooltip pageKey="reports" />
          </h1>
          <p className="text-gray-medium text-sm">Cross-module analytics with charts and exports</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {['7d', '30d', '90d', '1y'].map(r => (
            <Button key={r} size="sm" variant={quickRange === r ? 'default' : 'outline'}
              className={quickRange === r ? 'bg-emerald-brand hover:bg-emerald-hover text-white' : ''}
              onClick={() => applyQuickRange(r)} data-testid={`range-${r}`}>
              {r === '7d' ? '7 Days' : r === '30d' ? '30 Days' : r === '90d' ? '90 Days' : '1 Year'}
            </Button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 p-3 bg-white border border-gray-border rounded-lg">
        <div>
          <Label className="text-xs text-gray-500">From</Label>
          <Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setQuickRange(''); }} className="w-40 mt-1 h-8 text-sm" data-testid="report-date-from" />
        </div>
        <div>
          <Label className="text-xs text-gray-500">To</Label>
          <Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setQuickRange(''); }} className="w-40 mt-1 h-8 text-sm" data-testid="report-date-to" />
        </div>
        <Badge className="bg-gray-100 text-gray-600 text-[10px] h-6">{dateFrom} to {dateTo}</Badge>
      </div>

      <Tabs defaultValue="summary" className="space-y-4">
        <TabsList className="bg-white border border-gray-border w-full sm:w-auto overflow-x-auto flex-nowrap justify-start">
          <TabsTrigger value="summary" className="text-xs sm:text-sm whitespace-nowrap gap-1.5" data-testid="tab-summary"><BarChart3 className="h-3.5 w-3.5" /> Summary</TabsTrigger>
          <TabsTrigger value="revenue" className="text-xs sm:text-sm whitespace-nowrap gap-1.5" data-testid="tab-revenue"><DollarSign className="h-3.5 w-3.5" /> Revenue</TabsTrigger>
          <TabsTrigger value="attendance" className="text-xs sm:text-sm whitespace-nowrap gap-1.5" data-testid="tab-att-report"><Clock className="h-3.5 w-3.5" /> Attendance</TabsTrigger>
          <TabsTrigger value="field-jobs" className="text-xs sm:text-sm whitespace-nowrap gap-1.5" data-testid="tab-field-report"><MapPin className="h-3.5 w-3.5" /> Field Jobs</TabsTrigger>
          <TabsTrigger value="hr" className="text-xs sm:text-sm whitespace-nowrap gap-1.5" data-testid="tab-hr-report"><Users className="h-3.5 w-3.5" /> HR</TabsTrigger>
          <TabsTrigger value="tickets" className="text-xs sm:text-sm whitespace-nowrap gap-1.5" data-testid="tab-tickets-report"><Ticket className="h-3.5 w-3.5" /> Tickets</TabsTrigger>
        </TabsList>

        <TabsContent value="summary"><ExecutiveSummary dateFrom={dateFrom} dateTo={dateTo} /></TabsContent>
        <TabsContent value="revenue"><RevenueReport dateFrom={dateFrom} dateTo={dateTo} /></TabsContent>
        <TabsContent value="attendance"><AttendanceReport dateFrom={dateFrom} dateTo={dateTo} /></TabsContent>
        <TabsContent value="field-jobs"><FieldJobsReport dateFrom={dateFrom} dateTo={dateTo} /></TabsContent>
        <TabsContent value="hr"><HRReport /></TabsContent>
        <TabsContent value="tickets"><TicketsReport dateFrom={dateFrom} dateTo={dateTo} /></TabsContent>
      </Tabs>
    </div>
  );
}
